import React, { useEffect } from 'react';
import {
  Title,
  Group,
  Button,
  TextInput,
  Textarea,
  Select,
  Switch,
  Stack,
  Grid,
  Card,
  ActionIcon,
  LoadingOverlay,
  Alert,
  Text,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconCheck,
  IconX,
  IconAlertTriangle,
  IconSchool,
  IconMapPin,
  IconPhone,
  IconMail,
  IconUser,
  IconCalendar,
} from '@tabler/icons-react';
import { useForm } from '@mantine/form';
import { useNavigate, useParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useSchool, useCreateSchool, useUpdateSchool } from '../../hooks/useSchools';

interface SchoolFormData {
  name: string;
  school_type: 'elementary' | 'middle' | 'high' | 'kindergarten' | 'mixed';
  principal_name: string;
  principal_email?: string;
  email: string;
  phone: string;
  address: string;
  academic_year: string;
  active: boolean;
}

const SchoolForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = id !== 'new' && !!id;

  const {
    data: school,
    isLoading: schoolLoading,
    error: schoolError,
  } = useSchool(id || '', { enabled: isEditing });

  const createSchoolMutation = useCreateSchool();
  const updateSchoolMutation = useUpdateSchool();

  const form = useForm<SchoolFormData>({
    initialValues: {
      name: '',
      school_type: 'kindergarten' as const,
      principal_name: '',
      principal_email: '',
      email: '',
      phone: '',
      address: '',
      academic_year: '2024-2025',
      active: true,
    },
    validate: {
      name: (value) => (!value.trim() ? 'School name is required' : null),
      school_type: (value) => (!value ? 'School type is required' : null),
      email: (value) => {
        if (!value.trim()) return 'Email is required';
        if (!/^\S+@\S+\.\S+$/.test(value)) return 'Invalid email format';
        return null;
      },
      phone: (value) => {
        if (!value.trim()) return null;
        if (!/^[\+]?[0-9\-\s\(\)]+$/.test(value)) return 'Invalid phone format';
        return null;
      },
      principal_name: (value) => (!value.trim() ? 'Principal name is required' : null),
      academic_year: (value) => (!value.trim() ? 'Academic year is required' : null),
    },
  });

  // Populate form when editing
  useEffect(() => {
    if (isEditing && school) {
      form.setValues({
        name: school.name || '',
        school_type: school.school_type || 'kindergarten',
        principal_name: school.principal_name || '',
        principal_email: school.principal_email || '',
        email: school.email || '',
        phone: school.phone || '',
        address: school.address || '',
        academic_year: school.academic_year || '2024-2025',
        active: school.active ?? true,
      });
    }
  }, [school, isEditing]);

  const handleSubmit = async (values: SchoolFormData) => {
    try {
      if (isEditing) {
        await updateSchoolMutation.mutateAsync({
          id: id!,
          data: values,
        });
        notifications.show({
          title: 'Success',
          message: 'School updated successfully',
          color: 'green',
          icon: <IconCheck size={16} />,
        });
      } else {
        const submitData = {
          ...values,
          principal_email: values.principal_email || values.email, // Use school email as fallback
        };
        const response = await createSchoolMutation.mutateAsync(submitData);
        notifications.show({
          title: 'Success',
          message: 'School created successfully',
          color: 'green',
          icon: <IconCheck size={16} />,
        });
        navigate(`/admin/schools/${response.id}`);
        return;
      }
      
      navigate('/admin/schools');
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: isEditing ? 'Failed to update school' : 'Failed to create school',
        color: 'red',
        icon: <IconX size={16} />,
      });
    }
  };

  const handleCancel = () => {
    if (isEditing) {
      navigate(`/admin/schools/${id}`);
    } else {
      navigate('/admin/schools');
    }
  };

  const isLoading = createSchoolMutation.isPending || updateSchoolMutation.isPending;

  if (isEditing && schoolError) {
    return (
      <Alert
        icon={<IconAlertTriangle size={16} />}
        title="Error loading school"
        color="red"
      >
        Failed to load school details. Please try again.
      </Alert>
    );
  }

  if (isEditing && schoolLoading) {
    return <LoadingOverlay visible />;
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between">
          <Group>
            <ActionIcon variant="subtle" onClick={handleCancel}>
              <IconArrowLeft size={16} />
            </ActionIcon>
            <div>
              <Title order={2}>
                {isEditing ? 'Edit School' : 'Create New School'}
              </Title>
              <Text size="sm" c="dimmed">
                {isEditing ? 'Update school information' : 'Add a new school to the system'}
              </Text>
            </div>
          </Group>
          <Group>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isLoading}
              leftSection={<IconCheck size={16} />}
            >
              {isEditing ? 'Update School' : 'Create School'}
            </Button>
          </Group>
        </Group>

        <Grid>
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Stack gap="md">
              {/* Basic Information */}
              <Card>
                <Group mb="md">
                  <IconSchool size={20} color="blue" />
                  <Title order={4}>Basic Information</Title>
                </Group>

                <Stack gap="md">
                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 8 }}>
                      <TextInput
                        label="School Name"
                        placeholder="Enter school name"
                        required
                        {...form.getInputProps('name')}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 4 }}>
                      <Select
                        label="School Type"
                        placeholder="Select type"
                        required
                        data={[
                          { value: 'kindergarten', label: 'Kindergarten' },
                          { value: 'elementary', label: 'Elementary' },
                          { value: 'middle', label: 'Middle School' },
                          { value: 'high', label: 'High School' },
                          { value: 'mixed', label: 'Mixed Grades' },
                        ]}
                        {...form.getInputProps('school_type')}
                      />
                    </Grid.Col>
                  </Grid>

                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TextInput
                        label="Principal Name"
                        placeholder="Enter principal's name"
                        required
                        leftSection={<IconUser size={16} />}
                        {...form.getInputProps('principal_name')}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TextInput
                        label="Academic Year"
                        placeholder="e.g., 2024-2025"
                        required
                        leftSection={<IconCalendar size={16} />}
                        {...form.getInputProps('academic_year')}
                      />
                    </Grid.Col>
                  </Grid>

                  <TextInput
                    label="Principal Email"
                    placeholder="principal@example.com"
                    description="If not provided, school email will be used"
                    leftSection={<IconMail size={16} />}
                    {...form.getInputProps('principal_email')}
                  />

                  <Textarea
                    label="Address"
                    placeholder="Enter complete address"
                    leftSection={<IconMapPin size={16} />}
                    autosize
                    minRows={2}
                    maxRows={4}
                    {...form.getInputProps('address')}
                  />
                </Stack>
              </Card>

              {/* Contact Information */}
              <Card>
                <Group mb="md">
                  <IconMail size={20} color="green" />
                  <Title order={4}>Contact Information</Title>
                </Group>

                <Grid>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <TextInput
                      label="Email Address"
                      placeholder="school@example.com"
                      required
                      leftSection={<IconMail size={16} />}
                      {...form.getInputProps('email')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <TextInput
                      label="Phone Number"
                      placeholder="+1 (555) 123-4567"
                      leftSection={<IconPhone size={16} />}
                      {...form.getInputProps('phone')}
                    />
                  </Grid.Col>
                </Grid>
              </Card>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 4 }}>
            <Stack gap="md">
              {/* Status & Settings */}
              <Card>
                <Title order={4} mb="md">Status & Settings</Title>
                
                <Stack gap="md">
                  <Switch
                    label="Active School"
                    description="Students can be enrolled and cameras can be monitored"
                    {...form.getInputProps('active', { type: 'checkbox' })}
                  />
                </Stack>
              </Card>

              {/* Help Information */}
              <Card>
                <Title order={4} mb="md">Need Help?</Title>
                <Stack gap="xs">
                  <Text size="sm" c="dimmed">
                    • School name should be unique and descriptive
                  </Text>
                  <Text size="sm" c="dimmed">
                    • Contact information is used for notifications
                  </Text>
                  <Text size="sm" c="dimmed">
                    • Inactive schools won't appear in student enrollment
                  </Text>
                </Stack>
              </Card>

              {isEditing && school && (
                <Card>
                  <Title order={4} mb="md">School Information</Title>
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">Created</Text>
                      <Text size="sm">
                        {new Date(school.created_at).toLocaleDateString()}
                      </Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">Last Updated</Text>
                      <Text size="sm">
                        {new Date(school.updated_at).toLocaleDateString()}
                      </Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">Students</Text>
                      <Text size="sm">{school.student_count || school.students_count || 0}</Text>
                    </Group>
                  </Stack>
                </Card>
              )}
            </Stack>
          </Grid.Col>
        </Grid>
      </Stack>
    </form>
  );
};

export default SchoolForm;